<template>
  <!-- A record page in the Board Members Print Preview style (REQ-43): grey
       canvas, white sheet, MSDH eyebrow, title, PWS line, meta line, then the
       page's own content. On paper the canvas and sheet drop away and the MSDH
       logo appears. Used by BoardMemberRoster and the RTCR Site List / Report. -->
  <div class="record-sheet">
    <div v-if="$slots.toolbar" class="sheet-toolbar no-print">
      <slot name="toolbar"></slot>
    </div>

    <article class="sheet" :class="{ 'has-running-head': runningHead, 'is-dense': dense }">
      <!-- With runningHead the sheet is a real table on paper: Chrome, Edge and
           Firefox repeat a <thead> at the top of every page, and the full header
           in the body covers it on page 1 (REQ-43). On screen the table is
           laid out as plain blocks and the thead is hidden. -->
      <component :is="tag.table" class="sheet-frame">
        <thead v-if="runningHead" class="sheet-running-head print-only" aria-hidden="true">
          <tr>
            <td>
              <div class="sheet-running-inner">
                <span class="rh-title">{{ title }}</span>
                <span class="rh-system">
                  <span class="nums">{{ pws_id }}</span>
                  <span>{{ pws_name }}</span>
                </span>
              </div>
            </td>
          </tr>
        </thead>
        <component :is="tag.tbody">
          <component :is="tag.tr">
            <component :is="tag.td" class="sheet-body">
              <header class="sheet-head">
                <img
                  class="sheet-logo print-only"
                  src="https://pws.mswater.us/static/msdh_logo.png"
                  alt="Mississippi State Department of Health"
                />
                <div>
                  <div class="sheet-eyebrow">
                    Mississippi State Department of Health &middot; Bureau of Public Water Supply
                  </div>
                  <h1 class="sheet-title">{{ title }}</h1>
                  <div v-if="pws_id || pws_name" class="sheet-system">
                    <span class="sheet-pwsid">{{ pws_id }}</span>
                    <span>{{ pws_name }}</span>
                  </div>
                  <div v-if="$slots.meta" class="sheet-meta">
                    <slot name="meta"></slot>
                  </div>
                </div>
              </header>

              <slot></slot>

              <footer v-if="$slots.foot" class="sheet-foot">
                <slot name="foot"></slot>
              </footer>
            </component>
          </component>
        </component>
      </component>
    </article>
  </div>
</template>

<script>
export default {
  name: "RecordSheet",
  props: {
    title: { type: String, required: true },
    pws_id: { type: String, default: '' },
    pws_name: { type: String, default: '' },
    // print the title, PWS ID and name at the top of pages 2 and on
    runningHead: { type: Boolean, default: false },
    // a compact page-1 header on paper, for lists meant to fit one page
    dense: { type: Boolean, default: false },
  },
  computed: {
    // the frame is a table only when it carries a running head
    tag() {
      return this.runningHead
        ? { table: "table", tbody: "tbody", tr: "tr", td: "td" }
        : { table: "div", tbody: "div", tr: "div", td: "div" }
    },
  },
}
</script>

<!-- Not scoped: these classes style the pages' own slot content (summary,
     rows, stamps), which scoped rules cannot reach. Every selector starts at
     .record-sheet so nothing leaks to other pages. -->
<style>
@import url("https://fonts.googleapis.com/css2?family=Public+Sans:wght@400;600;700&display=swap");

.record-sheet {
  --ink: #1c2b36;
  --muted: #5b6b75;
  --rule: #d5dde2;
  --canvas: #eef2f4;
  --paper: #ffffff;
  --water: #0b5d8c;
  --water-bg: #e1eef6;
  --ok: #1d6b47;
  --ok-bg: #e3f1e9;
  --due: #7d5200;
  --due-bg: #fbf0d9;
  --missing: #9b2226;
  --missing-bg: #f8e2e2;

  font-family: "Public Sans", -apple-system, "Segoe UI", Roboto, sans-serif;
  color: var(--ink);
  background: var(--canvas);
  margin: 0 -15px;
  padding: 1rem 16px 3rem;
  text-align: left;
}

/* REQ-21: what the PWS typed shows in caps, on screen and paper only. Never on emails. */
.record-sheet .pws-caps { text-transform: uppercase; }
.record-sheet .nums { font-variant-numeric: tabular-nums; }
.record-sheet .print-only { display: none; }

.record-sheet .sheet-toolbar {
  max-width: 1040px;
  margin: 0 auto 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.5rem;
}
.record-sheet .sheet-toolbar .toolbar-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.record-sheet .sheet {
  max-width: 1040px;
  margin: 0 auto;
  background: var(--paper);
  border: 1px solid var(--rule);
  border-radius: 6px;
  padding: 2rem 2.25rem 1.5rem;
}

/* runningHead frame: on screen the table lays out as plain blocks */
@media screen {
  .record-sheet table.sheet-frame,
  .record-sheet table.sheet-frame > tbody,
  .record-sheet table.sheet-frame > tbody > tr,
  .record-sheet table.sheet-frame > tbody > tr > td {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
  }
}

/* ---------- header ---------- */
.record-sheet .sheet-head {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: 1.25rem;
  border-bottom: 3px solid var(--water);
}
.record-sheet .sheet-eyebrow {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--water);
}
.record-sheet .sheet-title {
  font-size: 1.9rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0.2rem 0 0.35rem;
}
.record-sheet .sheet-system {
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.record-sheet .sheet-pwsid {
  font-variant-numeric: tabular-nums;
  color: var(--muted);
}
.record-sheet .sheet-meta {
  margin-top: 0.4rem;
  font-size: 0.875rem;
  color: var(--muted);
  display: flex;
  flex-wrap: wrap;
  gap: 0 1.25rem;
}

/* ---------- section headings inside the sheet ---------- */
.record-sheet .sheet-section {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 1.75rem 0 0;
}
.record-sheet .sheet-section .dd-note { font-size: 0.9rem; }

/* ---------- summary strip ---------- */
.record-sheet .sheet-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
  margin: 1.25rem 0 0.5rem;
}
.record-sheet .sheet-summary > div {
  padding-left: 0.75rem;
  border-left: 4px solid var(--rule);
}
.record-sheet .sheet-summary dt,
.record-sheet .sheet-facts dt,
.record-sheet .sheet-colheads {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
}
.record-sheet .sheet-summary dd {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.record-sheet .sheet-summary .is-ok { border-left-color: var(--ok); }
.record-sheet .sheet-summary .is-due { border-left-color: var(--due); }
.record-sheet .sheet-summary .is-missing { border-left-color: var(--missing); }
.record-sheet .sheet-summary .is-new { border-left-color: var(--water); }
.record-sheet .dd-note { font-weight: 400; color: var(--muted); font-size: 0.85em; }

/* ---------- label / value facts ---------- */
.record-sheet .sheet-facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem 1.5rem;
  margin: 1.25rem 0 0;
}
.record-sheet .sheet-facts .is-wide { grid-column: 1 / -1; }
.record-sheet .sheet-facts dd { margin: 0.15rem 0 0; font-size: 0.95rem; }

/* ---------- table rows: the page sets --sheet-cols ---------- */
.record-sheet .sheet-row {
  display: grid;
  grid-template-columns: var(--sheet-cols, 1fr);
  gap: 1rem;
  padding: 0.9rem 0;
  border-bottom: 1px solid var(--rule);
  align-items: start;
}
.record-sheet .sheet-colheads {
  border-bottom: 1px solid var(--ink);
  padding-bottom: 0.4rem;
  margin-top: 1rem;
}
.record-sheet .sheet-colheads small {
  display: block;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}
.record-sheet .cell-label { display: none; }
.record-sheet .missing {
  color: var(--missing);
  font-weight: 600;
}
.record-sheet .sheet-empty {
  margin: 1.5rem 0;
  padding: 1.25rem;
  border: 1px dashed #c3ccd2;
  border-radius: 4px;
  text-align: center;
  font-size: 1.05rem;
}

/* The signature: a status reads as a stamp on the record. */
.record-sheet .stamp {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  padding: 0.3rem 0.55rem;
  border-radius: 4px;
  border: 1px solid transparent;
  font-variant-numeric: tabular-nums;
}
.record-sheet .stamp .b-icon { flex: 0 0 auto; margin-top: 0.1rem; }
.record-sheet .stamp.is-ok { color: var(--ok); background: var(--ok-bg); }
.record-sheet .stamp.is-due { color: var(--due); background: var(--due-bg); }
.record-sheet .stamp.is-missing { color: var(--missing); background: var(--missing-bg); }
.record-sheet .stamp.is-new { color: var(--water); background: var(--water-bg); }
.record-sheet .stamp.is-none { color: var(--muted); border-color: var(--rule); font-weight: 400; }

.record-sheet .sheet-foot {
  margin-top: 1.25rem;
  font-size: 0.8rem;
  color: var(--muted);
}
.record-sheet .sheet-foot p { margin: 0 0 0.35rem; }

/* ---------- narrow screens: each row becomes a card ---------- */
@media screen and (max-width: 767px) {
  .record-sheet .sheet { padding: 1.25rem 1rem 1rem; }
  .record-sheet .sheet-title { font-size: 1.5rem; }
  .record-sheet .sheet-facts { grid-template-columns: 1fr 1fr; }
  .record-sheet .sheet-colheads { display: none; }
  .record-sheet .sheet-row {
    grid-template-columns: 1fr;
    gap: 0.6rem;
  }
  /* A cell with a .cell-label shows as label | value. */
  .record-sheet .sheet-row > .labeled {
    display: grid;
    grid-template-columns: 6.5rem 1fr;
    align-items: start;
    column-gap: 0.5rem;
  }
  .record-sheet .cell-label {
    display: block;
    font-size: 0.8rem;
    color: var(--muted);
    padding-top: 0.05rem;
  }
  .record-sheet .labeled .stamp { justify-self: start; }
  .record-sheet .sheet-toolbar .btn { min-height: 44px; }
}
@media screen and (max-width: 575px) {
  .record-sheet .sheet-facts { grid-template-columns: 1fr; }
  .record-sheet .sheet-toolbar,
  .record-sheet .sheet-toolbar .toolbar-group { flex-direction: column; width: 100%; }
}

/* ---------- print: a plain record, black ink ---------- */
@media print {
  @page { size: letter portrait; margin: 0.55in 0.5in; }

  .record-sheet .no-print, .record-sheet .no-print * { display: none !important; }
  .record-sheet .print-only { display: initial; }
  .record-sheet img.print-only { display: block; }

  .record-sheet {
    background: none;
    margin: 0;
    padding: 0;
    font-size: 10pt;
  }
  .record-sheet .sheet {
    max-width: none;
    border: none;
    border-radius: 0;
    padding: 0;
  }
  .record-sheet .sheet-head { border-bottom-width: 2px; padding-bottom: 0.6rem; }
  .record-sheet .sheet-logo { height: 58px; width: auto; }
  .record-sheet .sheet-title { font-size: 18pt; margin: 0.1rem 0; }
  .record-sheet .sheet-system { font-size: 12pt; }
  .record-sheet .sheet-meta,
  .record-sheet .sheet-summary dd { font-size: 10pt; }
  .record-sheet .sheet-summary { margin: 0.6rem 0 0.2rem; gap: 0.2rem 1.5rem; }
  .record-sheet .sheet-summary > div { border-left-width: 3px; }
  .record-sheet .sheet-section { font-size: 12pt; margin-top: 0.9rem; break-after: avoid; }
  .record-sheet .sheet-facts { margin-top: 0.6rem; gap: 0.4rem 1rem; }
  .record-sheet .sheet-facts dd { font-size: 9.5pt; }

  .record-sheet .sheet-row {
    grid-template-columns: var(--sheet-cols-print, var(--sheet-cols, 1fr));
    gap: 0.6rem;
    padding: 0.45rem 0;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .record-sheet .sheet-colheads { margin-top: 0.4rem; font-size: 7.5pt; }

  /* On paper, anything not on file is a line to write it in. */
  .record-sheet .missing {
    color: transparent;
    display: inline-block;
    min-width: 7em;
    border-bottom: 1px solid var(--ink);
    height: 1.1em;
    overflow: hidden;
  }

  /* Stamps become plain marks: black ink reads on any printer. */
  .record-sheet .stamp {
    padding: 0;
    background: none !important;
    border: none;
    color: var(--ink) !important;
    font-size: 9pt;
  }
  .record-sheet .stamp::before {
    font-weight: 700;
    width: 1em;
    flex: 0 0 1em;
  }
  .record-sheet .stamp.is-ok::before { content: "\2713"; }
  .record-sheet .stamp.is-due::before { content: "!"; }
  .record-sheet .stamp.is-missing::before { content: "\2717"; }
  .record-sheet .stamp.is-new::before { content: "+"; }
  .record-sheet .stamp.is-none::before { content: "\2013"; }
  .record-sheet .stamp.is-none { font-weight: 400; }

  /* Running header on pages 2+ (runningHead): the frame prints as the
     table it is, so the thead repeats on every page. On page 1 the full
     header is pulled up over it and painted opaque. */
  .record-sheet .sheet.has-running-head { --running-head: 0.42in; }
  .record-sheet table.sheet-frame {
    display: table;
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }
  .record-sheet table.sheet-frame > thead { display: table-header-group; }
  .record-sheet table.sheet-frame > tbody { display: table-row-group; }
  .record-sheet table.sheet-frame > * > tr { display: table-row; }
  .record-sheet table.sheet-frame > * > tr > td {
    display: table-cell;
    padding: 0;
    border: 0;
    vertical-align: top;
  }
  .record-sheet .sheet-running-inner {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    height: calc(var(--running-head) - 0.14in);
    margin-bottom: 0.14in;
    padding-bottom: 0.08in;
    border-bottom: 1px solid var(--ink);
    font-size: 8.5pt;
    color: var(--muted);
  }
  .record-sheet .sheet-running-inner .rh-title { font-weight: 700; color: var(--ink); }
  .record-sheet .sheet-running-inner .rh-system { display: flex; gap: 0.5rem; }
  .record-sheet .sheet.has-running-head .sheet-head {
    position: relative;
    z-index: 1;
    background: var(--paper);
    margin-top: calc(-1 * var(--running-head));
  }

  /* dense: a compact page-1 header and running head, so a list gets the room */
  .record-sheet .sheet.is-dense.has-running-head { --running-head: 0.34in; }
  .record-sheet .sheet.is-dense .sheet-head {
    gap: 0.6rem;
    align-items: center;
    padding-bottom: 0.3rem;
    border-bottom-width: 1.5px;
  }
  .record-sheet .sheet.is-dense .sheet-logo { height: 40px; }
  .record-sheet .sheet.is-dense .sheet-eyebrow { font-size: 6.5pt; }
  .record-sheet .sheet.is-dense .sheet-title { font-size: 13pt; margin: 0; line-height: 1.2; }
  .record-sheet .sheet.is-dense .sheet-system { font-size: 10pt; gap: 0.4rem; }
  .record-sheet .sheet.is-dense .sheet-meta { font-size: 7.5pt; margin-top: 0.1rem; }
  .record-sheet .sheet.is-dense .sheet-running-inner {
    height: calc(var(--running-head) - 0.1in);
    margin-bottom: 0.1in;
    padding-bottom: 0.05in;
    font-size: 8pt;
  }
  .record-sheet .sheet.is-dense .sheet-foot { margin-top: 0.3rem; font-size: 7pt; }
  .record-sheet .sheet.is-dense .sheet-foot p { margin: 0 0 0.1rem; }

  .record-sheet .sheet-empty { border-style: solid; }
  .record-sheet .sheet-foot { margin-top: 0.6rem; font-size: 8pt; }
}
</style>
